import { useState, useMemo, useEffect } from 'react'
import { useDepartment } from '../../context/DepartmentContext'
import {
  getShiftLogs, saveShiftLog, deleteShiftLog,
  getStaffPerformance,
  getHandoverNotes, saveHandoverNote, deleteHandoverNote,
  getDailyChecklist, saveDailyChecklist, DEFAULT_CHECKLIST,
  getProductionEfficiency,
  refreshStaffOps, refreshDailyChecklist,
} from '../../lib/staffOps'
import {
  Users, Clock, ClipboardList, MessageSquare, Plus, Trash2, Edit2,
  CheckCircle, XCircle, TrendingUp, Calendar, Save, AlertTriangle,
  X, ChevronDown, ChevronUp, BarChart3
} from 'lucide-react'

const TABS = [
  { id: 'shifts', label: 'Shift Log', icon: Clock },
  { id: 'performance', label: 'Staff Performance', icon: TrendingUp },
  { id: 'handover', label: 'Handover Notes', icon: MessageSquare },
  { id: 'checklist', label: 'Daily Checklist', icon: ClipboardList },
]

const SHIFT_OPTIONS = ['morning', 'afternoon', 'evening']
const HANDOVER_CATEGORIES = ['general', 'urgent', 'followup', 'info']

function today() { return new Date().toISOString().split('T')[0] }
function daysAgo(n) { return new Date(Date.now() - n * 86400000).toISOString().split('T')[0] }

const EMPTY_SHIFT = {
  staffName: '', shift: 'morning', date: '', hoursWorked: '',
  salesProcessed: '', unitsProduced: '', plannedUnits: '', notes: ''
}

const EMPTY_HANDOVER = {
  author: '', category: 'general', content: '', date: ''
}

export default function StaffOps() {
  const { currentDepartmentId } = useDepartment()
  const [tab, setTab] = useState('shifts')
  const [refresh, setRefresh] = useState(0)
  const bump = () => setRefresh(r => r + 1)

  // ── Shift Log state ──
  const [shiftDate, setShiftDate] = useState(today())
  const [shiftForm, setShiftForm] = useState({ ...EMPTY_SHIFT, date: today() })
  const [editingShiftId, setEditingShiftId] = useState(null)
  const [showShiftForm, setShowShiftForm] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  // ── Performance state ──
  const [perfFrom, setPerfFrom] = useState(daysAgo(30))
  const [perfTo, setPerfTo] = useState(today())

  // ── Handover state ──
  const [handoverDate, setHandoverDate] = useState(today())
  const [handoverForm, setHandoverForm] = useState({ ...EMPTY_HANDOVER, date: today() })
  const [showHandoverForm, setShowHandoverForm] = useState(false)

  // ── Checklist state ──
  const [checklistDate, setChecklistDate] = useState(today())

  // ── Cross-device sync on mount + when department changes ──
  // Pulls latest shift logs, handover notes, and today's checklist
  // from Firestore so data saved on Device A is visible on Device B.
  useEffect(() => {
    if (!currentDepartmentId) return
    let cancelled = false
    ;(async () => {
      await refreshStaffOps(currentDepartmentId)
      if (!cancelled) bump()
    })()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDepartmentId])

  useEffect(() => {
    if (!currentDepartmentId || !checklistDate) return
    let cancelled = false
    ;(async () => {
      await refreshDailyChecklist(currentDepartmentId, checklistDate)
      if (!cancelled) bump()
    })()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDepartmentId, checklistDate])

  // ════════════════════════════════════════
  // DATA
  // ════════════════════════════════════════

  const shifts = useMemo(() => {
    void refresh
    return getShiftLogs(currentDepartmentId, shiftDate)
  }, [currentDepartmentId, shiftDate, refresh])

  const perfData = useMemo(() => {
    void refresh
    return getStaffPerformance(currentDepartmentId, perfFrom, perfTo)
  }, [currentDepartmentId, perfFrom, perfTo, refresh])

  const efficiencyData = useMemo(() => {
    void refresh
    return getProductionEfficiency(currentDepartmentId, perfFrom, perfTo)
  }, [currentDepartmentId, perfFrom, perfTo, refresh])

  const handoverNotes = useMemo(() => {
    void refresh
    return getHandoverNotes(currentDepartmentId, handoverDate)
  }, [currentDepartmentId, handoverDate, refresh])

  const checklist = useMemo(() => {
    void refresh
    return getDailyChecklist(currentDepartmentId, checklistDate)
  }, [currentDepartmentId, checklistDate, refresh])

  const checklistProgress = useMemo(() => {
    const total = checklist.length
    const completed = checklist.filter(c => c.completed).length
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0
    return { total, completed, pct }
  }, [checklist])

  // ════════════════════════════════════════
  // HANDLERS
  // ════════════════════════════════════════

  function handleSaveShift() {
    if (!shiftForm.staffName.trim()) return
    const record = {
      ...(editingShiftId ? { id: editingShiftId } : {}),
      departmentId: currentDepartmentId,
      date: shiftForm.date || shiftDate,
      staffName: shiftForm.staffName.trim(),
      shift: shiftForm.shift,
      hoursWorked: Number(shiftForm.hoursWorked) || 0,
      salesProcessed: Number(shiftForm.salesProcessed) || 0,
      unitsProduced: Number(shiftForm.unitsProduced) || 0,
      plannedUnits: Number(shiftForm.plannedUnits) || 0,
      notes: shiftForm.notes,
    }
    saveShiftLog(record)
    setShiftForm({ ...EMPTY_SHIFT, date: shiftDate })
    setEditingShiftId(null)
    setShowShiftForm(false)
    bump()
  }

  function handleEditShift(shift) {
    setEditingShiftId(shift.id)
    setShiftForm({
      staffName: shift.staffName,
      shift: shift.shift,
      date: shift.date || shiftDate,
      hoursWorked: shift.hoursWorked || '',
      salesProcessed: shift.salesProcessed || '',
      unitsProduced: shift.unitsProduced || '',
      plannedUnits: shift.plannedUnits || '',
      notes: shift.notes || '',
    })
    setShowShiftForm(true)
  }

  function handleDeleteShift(id) {
    if (deleteConfirm === id) {
      deleteShiftLog(id)
      setDeleteConfirm(null)
      bump()
    } else {
      setDeleteConfirm(id)
    }
  }

  function handleCancelShiftForm() {
    setEditingShiftId(null)
    setShiftForm({ ...EMPTY_SHIFT, date: shiftDate })
    setShowShiftForm(false)
  }

  function handleSaveHandover() {
    if (!handoverForm.content.trim()) return
    saveHandoverNote({
      departmentId: currentDepartmentId,
      date: handoverForm.date || handoverDate,
      content: handoverForm.content.trim(),
      category: handoverForm.category,
      author: handoverForm.author.trim() || 'Staff',
    })
    setHandoverForm({ ...EMPTY_HANDOVER, date: handoverDate })
    setShowHandoverForm(false)
    bump()
  }

  function handleDeleteHandover(id) {
    if (deleteConfirm === id) {
      deleteHandoverNote(id)
      setDeleteConfirm(null)
      bump()
    } else {
      setDeleteConfirm(id)
    }
  }

  function handleToggleChecklist(index) {
    const updated = checklist.map((item, i) => {
      if (i !== index) return item
      if (item.completed) {
        return { ...item, completed: false, completedBy: null, completedAt: null }
      }
      return {
        ...item,
        completed: true,
        completedBy: 'Current User',
        completedAt: new Date().toISOString(),
      }
    })
    saveDailyChecklist(currentDepartmentId, checklistDate, updated)
    bump()
  }

  // ════════════════════════════════════════
  // STYLES
  // ════════════════════════════════════════

  const inputCls = 'bg-gray-800 border border-gray-700 text-sm text-white rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 w-full'
  const btnPrimary = 'bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg px-4 py-2 flex items-center gap-2 transition-colors'
  const btnDanger = 'text-red-400 hover:text-red-300 transition-colors'
  const btnGhost = 'text-gray-400 hover:text-gray-200 text-sm px-4 py-2 transition-colors'
  const card = 'bg-gray-900 border border-gray-800 rounded-lg p-4'

  function effStatusBadge(status) {
    const map = {
      excellent: { bg: 'bg-green-900/50 text-green-400 border-green-700', label: 'Excellent' },
      good: { bg: 'bg-blue-900/50 text-blue-400 border-blue-700', label: 'Good' },
      needs_improvement: { bg: 'bg-yellow-900/50 text-yellow-400 border-yellow-700', label: 'Needs Improvement' },
      critical: { bg: 'bg-red-900/50 text-red-400 border-red-700', label: 'Critical' },
    }
    return map[status] || map.critical
  }

  function categoryLabel(cat) {
    const map = { general: 'General', urgent: 'Urgent', followup: 'Follow-up', info: 'Info' }
    return map[cat] || cat
  }

  function shiftLabel(s) {
    const map = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' }
    return map[s] || s
  }

  // ════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-950 px-4 sm:px-6 py-5">
        <div className="flex items-center gap-3 mb-1">
          <Users size={22} className="text-blue-400" />
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white">Staff Operations</h1>
        </div>
        <p className="text-gray-400 text-sm">Shift logs, performance tracking, handover notes, and daily checklists</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-800 px-4 sm:px-6">
        <div className="flex gap-0 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); setDeleteConfirm(null) }}
                className={`px-4 sm:px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                  tab === t.id
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-200'
                }`}
              >
                <Icon size={15} />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6 max-w-7xl">

        {/* ══════════════════════════════════════ */}
        {/* TAB 1: Shift Log                      */}
        {/* ══════════════════════════════════════ */}
        {tab === 'shifts' && (
          <div>
            {/* Date filter + Add button */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <Calendar size={16} className="text-gray-400" />
              <label className="text-sm text-gray-400">Date:</label>
              <input
                type="date"
                value={shiftDate}
                onChange={e => { setShiftDate(e.target.value); setShiftForm(f => ({ ...f, date: e.target.value })) }}
                className={inputCls + ' !w-auto'}
              />
              <button
                onClick={() => { setShowShiftForm(true); setEditingShiftId(null); setShiftForm({ ...EMPTY_SHIFT, date: shiftDate }) }}
                className={btnPrimary}
              >
                <Plus size={15} />
                Add Shift
              </button>
            </div>

            {/* Add / Edit form (inline expansion) */}
            {showShiftForm && (
              <div className={card + ' mb-6'}>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-semibold text-white flex items-center gap-2">
                    {editingShiftId ? <Edit2 size={15} className="text-blue-400" /> : <Plus size={15} className="text-blue-400" />}
                    {editingShiftId ? 'Edit Shift Log' : 'New Shift Log'}
                  </p>
                  <button onClick={handleCancelShiftForm} className="text-gray-400 hover:text-white">
                    <X size={18} />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Staff Name *</label>
                    <input
                      type="text"
                      placeholder="e.g. John"
                      value={shiftForm.staffName}
                      onChange={e => setShiftForm(f => ({ ...f, staffName: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Shift</label>
                    <select
                      value={shiftForm.shift}
                      onChange={e => setShiftForm(f => ({ ...f, shift: e.target.value }))}
                      className={inputCls}
                    >
                      {SHIFT_OPTIONS.map(s => <option key={s} value={s}>{shiftLabel(s)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Date</label>
                    <input
                      type="date"
                      value={shiftForm.date}
                      onChange={e => setShiftForm(f => ({ ...f, date: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Hours Worked</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      placeholder="8"
                      value={shiftForm.hoursWorked}
                      onChange={e => setShiftForm(f => ({ ...f, hoursWorked: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Sales Processed</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={shiftForm.salesProcessed}
                      onChange={e => setShiftForm(f => ({ ...f, salesProcessed: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Units Produced</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={shiftForm.unitsProduced}
                      onChange={e => setShiftForm(f => ({ ...f, unitsProduced: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Planned Units</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={shiftForm.plannedUnits}
                      onChange={e => setShiftForm(f => ({ ...f, plannedUnits: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Notes</label>
                    <input
                      type="text"
                      placeholder="Optional notes..."
                      value={shiftForm.notes}
                      onChange={e => setShiftForm(f => ({ ...f, notes: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleSaveShift} className={btnPrimary}>
                    <Save size={15} />
                    {editingShiftId ? 'Update Shift' : 'Save Shift'}
                  </button>
                  <button onClick={handleCancelShiftForm} className={btnGhost}>Cancel</button>
                </div>
              </div>
            )}

            {/* Shifts list */}
            {shifts.length === 0 ? (
              <p className="text-gray-500 text-sm">No shifts logged for {shiftDate}.</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                  {shifts.length} shift{shifts.length !== 1 ? 's' : ''} on {shiftDate}
                </p>
                {shifts.map(s => (
                  <div key={s.id} className={card + ' flex flex-col sm:flex-row sm:items-start justify-between gap-3'}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <span className="text-white font-medium">{s.staffName}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-300 border border-gray-700">
                          {shiftLabel(s.shift)}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                        <span>{s.hoursWorked || 0}h worked</span>
                        <span>{s.salesProcessed || 0} sales</span>
                        <span>{s.unitsProduced || 0} units produced</span>
                        {s.plannedUnits > 0 && <span>{s.plannedUnits} planned</span>}
                      </div>
                      {s.notes && <p className="text-xs text-gray-500 mt-1">{s.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => handleEditShift(s)} className="text-gray-400 hover:text-blue-400 transition-colors">
                        <Edit2 size={15} />
                      </button>
                      {deleteConfirm === s.id ? (
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleDeleteShift(s.id)} className="text-xs text-red-400 hover:text-red-300 font-medium">
                            Confirm
                          </button>
                          <button onClick={() => setDeleteConfirm(null)} className="text-xs text-gray-500 hover:text-gray-300">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => handleDeleteShift(s.id)} className={btnDanger}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 2: Staff Performance               */}
        {/* ══════════════════════════════════════ */}
        {tab === 'performance' && (
          <div>
            {/* Date range */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <Calendar size={16} className="text-gray-400" />
              <label className="text-sm text-gray-400">From:</label>
              <input
                type="date"
                value={perfFrom}
                onChange={e => setPerfFrom(e.target.value)}
                className={inputCls + ' !w-auto'}
              />
              <label className="text-sm text-gray-400">To:</label>
              <input
                type="date"
                value={perfTo}
                onChange={e => setPerfTo(e.target.value)}
                className={inputCls + ' !w-auto'}
              />
            </div>

            {/* Summary stat cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Total Staff</p>
                <p className="text-xl font-bold text-white">{perfData.length}</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Total Shifts</p>
                <p className="text-xl font-bold text-white">{perfData.reduce((s, p) => s + p.shifts, 0)}</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Total Hours</p>
                <p className="text-xl font-bold text-white">{perfData.reduce((s, p) => s + p.totalHours, 0).toFixed(1)}</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Avg Hours/Shift</p>
                <p className="text-xl font-bold text-white">
                  {(() => {
                    const ts = perfData.reduce((s, p) => s + p.shifts, 0)
                    const th = perfData.reduce((s, p) => s + p.totalHours, 0)
                    return ts > 0 ? (th / ts).toFixed(1) : '0'
                  })()}
                </p>
              </div>
            </div>

            {/* Staff performance table */}
            {perfData.length === 0 ? (
              <p className="text-gray-500 text-sm mb-8">No shift data in this date range.</p>
            ) : (
              <div className="overflow-x-auto mb-8">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800 text-left">
                      <th className="py-2 pr-4 text-gray-400 font-medium">Staff Name</th>
                      <th className="py-2 pr-4 text-gray-400 font-medium">Total Shifts</th>
                      <th className="py-2 pr-4 text-gray-400 font-medium">Total Hours</th>
                      <th className="py-2 pr-4 text-gray-400 font-medium">Avg Hrs/Shift</th>
                      <th className="py-2 pr-4 text-gray-400 font-medium">Sales</th>
                      <th className="py-2 pr-4 text-gray-400 font-medium">Sales/Hr</th>
                      <th className="py-2 pr-4 text-gray-400 font-medium">Units</th>
                      <th className="py-2 pr-4 text-gray-400 font-medium">Units/Hr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perfData.map(p => (
                      <tr key={p.staffName} className="border-b border-gray-800/50 hover:bg-gray-900/50">
                        <td className="py-2 pr-4 text-white font-medium">{p.staffName}</td>
                        <td className="py-2 pr-4 text-gray-300">{p.shifts}</td>
                        <td className="py-2 pr-4 text-gray-300">{p.totalHours}</td>
                        <td className="py-2 pr-4 text-gray-300">{p.avgHoursPerShift}</td>
                        <td className="py-2 pr-4 text-gray-300">{p.salesProcessed}</td>
                        <td className="py-2 pr-4 text-gray-300">{p.salesPerHour}</td>
                        <td className="py-2 pr-4 text-gray-300">{p.unitsProduced}</td>
                        <td className="py-2 pr-4 text-gray-300">{p.unitsPerHour}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Production Efficiency */}
            <div className="border-t border-gray-800 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 size={16} className="text-blue-400" />
                <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Production Efficiency</h3>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Planned</p>
                  <p className="text-xl font-bold text-white">{efficiencyData.totalPlanned}</p>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Actual</p>
                  <p className="text-xl font-bold text-white">{efficiencyData.totalActual}</p>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Efficiency</p>
                  <p className="text-xl font-bold text-white">{efficiencyData.efficiency}%</p>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <span className={`inline-block text-xs font-medium px-2 py-1 rounded border ${effStatusBadge(efficiencyData.status).bg}`}>
                    {effStatusBadge(efficiencyData.status).label}
                  </span>
                </div>
              </div>

              {/* Daily breakdown */}
              {efficiencyData.daily && efficiencyData.daily.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 text-left">
                        <th className="py-2 pr-4 text-gray-400 font-medium">Date</th>
                        <th className="py-2 pr-4 text-gray-400 font-medium">Planned</th>
                        <th className="py-2 pr-4 text-gray-400 font-medium">Actual</th>
                        <th className="py-2 pr-4 text-gray-400 font-medium">Efficiency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {efficiencyData.daily.map(d => (
                        <tr key={d.date} className="border-b border-gray-800/50 hover:bg-gray-900/50">
                          <td className="py-2 pr-4 text-gray-300">{d.date}</td>
                          <td className="py-2 pr-4 text-gray-300">{d.planned}</td>
                          <td className="py-2 pr-4 text-gray-300">{d.actual}</td>
                          <td className="py-2 pr-4 text-gray-300">{d.efficiency}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(!efficiencyData.daily || efficiencyData.daily.length === 0) && (
                <p className="text-gray-500 text-sm">No production data for this period.</p>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 3: Handover Notes                  */}
        {/* ══════════════════════════════════════ */}
        {tab === 'handover' && (
          <div>
            {/* Date filter + Add button */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <Calendar size={16} className="text-gray-400" />
              <label className="text-sm text-gray-400">Date:</label>
              <input
                type="date"
                value={handoverDate}
                onChange={e => setHandoverDate(e.target.value)}
                className={inputCls + ' !w-auto'}
              />
              <button
                onClick={() => { setShowHandoverForm(true); setHandoverForm({ ...EMPTY_HANDOVER, date: handoverDate }) }}
                className={btnPrimary}
              >
                <Plus size={15} />
                Add Note
              </button>
            </div>

            {/* Add form (inline expansion) */}
            {showHandoverForm && (
              <div className={card + ' mb-6'}>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-semibold text-white flex items-center gap-2">
                    <Plus size={15} className="text-blue-400" />
                    New Handover Note
                  </p>
                  <button onClick={() => setShowHandoverForm(false)} className="text-gray-400 hover:text-white">
                    <X size={18} />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Author</label>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={handoverForm.author}
                      onChange={e => setHandoverForm(f => ({ ...f, author: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Category</label>
                    <select
                      value={handoverForm.category}
                      onChange={e => setHandoverForm(f => ({ ...f, category: e.target.value }))}
                      className={inputCls}
                    >
                      {HANDOVER_CATEGORIES.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Date</label>
                    <input
                      type="date"
                      value={handoverForm.date}
                      onChange={e => setHandoverForm(f => ({ ...f, date: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-500 mb-1 block">Content *</label>
                  <textarea
                    rows={4}
                    placeholder="What happened today? Issues, tomorrow's priorities..."
                    value={handoverForm.content}
                    onChange={e => setHandoverForm(f => ({ ...f, content: e.target.value }))}
                    className={inputCls + ' resize-y'}
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleSaveHandover} className={btnPrimary}>
                    <Save size={15} />
                    Save Note
                  </button>
                  <button onClick={() => setShowHandoverForm(false)} className={btnGhost}>Cancel</button>
                </div>
              </div>
            )}

            {/* Notes list */}
            {handoverNotes.length === 0 ? (
              <p className="text-gray-500 text-sm">No handover notes for {handoverDate}.</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">
                  {handoverNotes.length} note{handoverNotes.length !== 1 ? 's' : ''} for {handoverDate}
                </p>
                {handoverNotes.map(n => (
                  <div
                    key={n.id}
                    className={`bg-gray-900 rounded-lg p-4 flex flex-col sm:flex-row sm:items-start justify-between gap-3 border ${
                      n.category === 'urgent' ? 'border-red-600' : 'border-gray-800'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <span className="text-white text-sm font-medium">{n.author || 'Staff'}</span>
                        <span className={`text-xs px-2 py-0.5 rounded border ${
                          n.category === 'urgent'
                            ? 'bg-red-900/50 text-red-400 border-red-700'
                            : n.category === 'followup'
                            ? 'bg-yellow-900/50 text-yellow-400 border-yellow-700'
                            : n.category === 'info'
                            ? 'bg-blue-900/50 text-blue-400 border-blue-700'
                            : 'bg-gray-800 text-gray-300 border-gray-700'
                        }`}>
                          {categoryLabel(n.category)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {n.createdAt ? new Date(n.createdAt).toLocaleTimeString() : ''}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap mt-1">{n.content || n.text}</p>
                    </div>
                    <div className="shrink-0">
                      {deleteConfirm === n.id ? (
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleDeleteHandover(n.id)} className="text-xs text-red-400 hover:text-red-300 font-medium">
                            Confirm
                          </button>
                          <button onClick={() => setDeleteConfirm(null)} className="text-xs text-gray-500 hover:text-gray-300">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => handleDeleteHandover(n.id)} className={btnDanger}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 4: Daily Checklist                 */}
        {/* ══════════════════════════════════════ */}
        {tab === 'checklist' && (
          <div>
            {/* Date picker */}
            <div className="flex items-center gap-3 mb-6">
              <Calendar size={16} className="text-gray-400" />
              <label className="text-sm text-gray-400">Date:</label>
              <input
                type="date"
                value={checklistDate}
                onChange={e => setChecklistDate(e.target.value)}
                className={inputCls + ' !w-auto'}
              />
            </div>

            {/* Progress bar */}
            <div className={card + ' mb-6'}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-400">
                  Progress: <span className="text-white font-medium">{checklistProgress.completed}/{checklistProgress.total}</span> completed
                </p>
                <span className={`text-sm font-bold ${
                  checklistProgress.pct === 100 ? 'text-green-400' : checklistProgress.pct >= 50 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {checklistProgress.pct}%
                </span>
              </div>
              <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    checklistProgress.pct === 100 ? 'bg-green-500' : checklistProgress.pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${checklistProgress.pct}%` }}
                />
              </div>
            </div>

            {/* Checklist items */}
            <div className="space-y-1 mb-6">
              {checklist.map((item, i) => (
                <div
                  key={item.key}
                  onClick={() => handleToggleChecklist(i)}
                  className={`${card} flex items-center gap-3 cursor-pointer hover:bg-gray-800/50 transition-colors`}
                >
                  <div className={`w-5 h-5 rounded flex items-center justify-center border ${
                    item.completed ? 'bg-blue-600 border-blue-600' : 'border-gray-700'
                  }`}>
                    {item.completed && <CheckCircle size={14} className="text-white" />}
                  </div>
                  <div className="flex-1">
                    <span className={`text-sm ${item.completed ? 'text-gray-500 line-through' : 'text-gray-200'}`}>
                      {item.label}
                      {item.required && <span className="text-red-400 ml-1">*</span>}
                    </span>
                    {item.completed && item.completedBy && (
                      <span className="text-xs text-gray-600 ml-2">
                        -- {item.completedBy} at {item.completedAt ? new Date(item.completedAt).toLocaleTimeString() : '--'}
                      </span>
                    )}
                  </div>
                  {item.required && !item.completed && (
                    <span className="text-xs text-red-400 font-medium shrink-0">Required</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
